import { addMonths, format, lastDayOfMonth, parseISO } from 'date-fns';

const HALF_FIRST = 'first_half';
const HALF_END = 'end_month';

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const parseDate = (value) => {
  if (!value) return null;
  try {
    if (value instanceof Date) {
      return Number.isNaN(value?.getTime?.()) ? null : value;
    }
    const raw = String(value).trim();
    if (!raw) return null;
    const head = raw.length >= 10 ? raw.slice(0, 10) : raw;
    const isoCandidate = head;
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) {
      const parsed = parseISO(isoCandidate);
      return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
    }
    const mdY = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdY) {
      const month = Number(mdY[1]);
      const day = Number(mdY[2]);
      const year = Number(mdY[3]);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
    }
    const yMd = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (yMd) {
      const year = Number(yMd[1]);
      const month = Number(yMd[2]);
      const day = Number(yMd[3]);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
    }
    const parsed = parseISO(raw);
    return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
  } catch {
    return null;
  }
};

const toIso = (value) => {
  const date = parseDate(value);
  return date ? format(date, 'yyyy-MM-dd') : '';
};

const normalizeEmployeeId = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) return raw.replace(/^0+(?=\d)/, '');
  return raw;
};

const normalizeEmployeeName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const getLastDayDate = (date) => {
  return lastDayOfMonth(date).getDate();
};

const matchesMonthlyTarget = (date, target) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  if (target === HALF_FIRST) return date.getDate() === 15;
  if (target === HALF_END) return date.getDate() === getLastDayDate(date);
  return false;
};

const buildPayrollEventCandidates = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return [];
  const year = date.getFullYear();
  const month = date.getMonth();
  return [
    { half: HALF_FIRST, date: new Date(year, month, 15) },
    { half: HALF_END, date: lastDayOfMonth(date) }
  ];
};

const getNextPayrollEvent = (cursor, allowedHalves) => {
  let probe = parseDate(cursor);
  if (!probe) return null;
  for (let i = 0; i < 240; i += 1) {
    const candidates = buildPayrollEventCandidates(probe)
      .filter((item) => allowedHalves.has(item.half))
      .filter((item) => item.date >= probe)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    if (candidates.length > 0) return candidates[0];
    probe = new Date(probe.getFullYear(), probe.getMonth() + 1, 1);
  }
  return null;
};

export const getPayrollRecordHalf = (endISO) => {
  const endDate = parseDate(endISO);
  if (!endDate) return '';
  return endDate.getDate() <= 15 ? HALF_FIRST : HALF_END;
};

export const computeEffectiveLoanRange = ({ source, startISO, endISO }) => {
  const start = String(startISO || '').trim();
  const end = String(endISO || '').trim();
  const endDate = parseDate(end);
  if (!start || !end || !endDate) return { start, end };

  if (source === 'payroll') {
    const half = getPayrollRecordHalf(end);
    if (half === HALF_FIRST) {
      return { start, end: format(new Date(endDate.getFullYear(), endDate.getMonth(), 15), 'yyyy-MM-dd') };
    }
    return { start, end: format(lastDayOfMonth(endDate), 'yyyy-MM-dd') };
  }

  if (source === 'mf') {
    return {
      start: format(new Date(endDate.getFullYear(), endDate.getMonth(), 1), 'yyyy-MM-dd'),
      end: format(lastDayOfMonth(endDate), 'yyyy-MM-dd')
    };
  }

  return { start, end };
};

export const getPayrollScheduleMeta = (loan) => {
  const source = String(loan?.deduct_from || '').trim().toLowerCase();
  const frequency = String(loan?.payroll_frequency || '').trim().toLowerCase();
  const onceTargetRaw = String(loan?.payroll_once_month_target || '').trim().toLowerCase();
  const onceTarget = onceTargetRaw === '15th' ? HALF_FIRST : (onceTargetRaw === 'end_month' ? HALF_END : '');
  const amount15th = toNumber(loan?.payroll_deduct_15th);
  const amountEnd = toNumber(loan?.payroll_deduct_end_of_month);
  const sharedAmount = toNumber(loan?.deduct_per_cutoff);

  if (source !== 'payroll') {
    return {
      source,
      kind: source === 'mf' ? 'mf' : 'other',
      onceTarget: '',
      amount15th: 0,
      amountEnd: 0,
      sharedAmount
    };
  }

  if (frequency === 'once_month' && onceTarget) {
    return {
      source,
      kind: 'once_month',
      onceTarget,
      amount15th: onceTarget === HALF_FIRST ? sharedAmount : 0,
      amountEnd: onceTarget === HALF_END ? sharedAmount : 0,
      sharedAmount
    };
  }

  if (frequency === 'twice_month') {
    return {
      source,
      kind: 'twice_month',
      onceTarget: '',
      amount15th,
      amountEnd,
      sharedAmount
    };
  }

  return {
    source,
    kind: 'legacy_payroll',
    onceTarget: '',
    amount15th: sharedAmount,
    amountEnd: sharedAmount,
    sharedAmount
  };
};

export const getLoanScheduleSummaryLabel = (loan) => {
  const meta = getPayrollScheduleMeta(loan);
  if (meta.kind === 'mf') return 'MF every month';
  if (meta.kind === 'once_month') {
    return meta.onceTarget === HALF_FIRST ? 'Payroll once a month - Every 15th' : 'Payroll once a month - Every End of Month';
  }
  if (meta.kind === 'twice_month') return 'Payroll twice a month';
  if (meta.kind === 'legacy_payroll') return 'Payroll every cutoff';
  return '-';
};

export const getLoanDeductionAmountForRecord = ({ loan, record, source }) => {
  const recordSource = String(source || loan?.deduct_from || '').trim().toLowerCase();
  const meta = getPayrollScheduleMeta({ ...loan, deduct_from: recordSource });
  const range = computeEffectiveLoanRange({
    source: recordSource,
    startISO: record?.start_date,
    endISO: record?.end_date
  });
  const effectiveStart = String(range.start || '').trim();
  const effectiveEnd = String(range.end || '').trim();
  const loanStart = String(loan?.start_date || '').trim();
  const loanEnd = String(loan?.end_date || '').trim();
  const effectiveStartDate = parseDate(effectiveStart);
  const effectiveEndDate = parseDate(effectiveEnd);
  const loanStartDate = parseDate(loanStart);
  const loanEndDate = parseDate(loanEnd);
  if (!effectiveStartDate || !effectiveEndDate || !loanStartDate || !loanEndDate) return 0;
  if (!(loanStartDate <= effectiveEndDate && loanEndDate >= effectiveStartDate)) return 0;

  if (meta.kind === 'mf') return meta.sharedAmount;
  if (meta.kind === 'legacy_payroll') return meta.sharedAmount;

  const half = getPayrollRecordHalf(record?.end_date || effectiveEnd);
  if (meta.kind === 'once_month') {
    return meta.onceTarget === half ? meta.sharedAmount : 0;
  }
  if (meta.kind === 'twice_month') {
    return half === HALF_FIRST ? meta.amount15th : meta.amountEnd;
  }
  return 0;
};

export const doesLoanMatchEmployee = ({ loan, employeeId, employeeName }) => {
  const normalizedEmployeeId = normalizeEmployeeId(employeeId);
  const normalizedLoanId = normalizeEmployeeId(loan?.employee_id);
  if (normalizedEmployeeId && normalizedLoanId) return normalizedEmployeeId === normalizedLoanId;

  const normalizedEmployeeName = normalizeEmployeeName(employeeName);
  const normalizedLoanName = normalizeEmployeeName(loan?.employee_name);
  if (!normalizedEmployeeName || !normalizedLoanName) return false;
  return normalizedLoanName.includes(normalizedEmployeeName) || normalizedEmployeeName.includes(normalizedLoanName);
};

export const getApplicableLoansForRecord = ({
  loans,
  employeeId,
  employeeName,
  record,
  source
}) => {
  const list = Array.isArray(loans) ? loans : [];
  const recordSource = String(source || '').trim().toLowerCase();
  const range = computeEffectiveLoanRange({
    source: recordSource,
    startISO: record?.start_date,
    endISO: record?.end_date
  });
  const effectiveStart = String(range.start || '').trim();
  const effectiveEnd = String(range.end || '').trim();
  const effectiveStartDate = parseDate(effectiveStart);
  const effectiveEndDate = parseDate(effectiveEnd);
  if (!effectiveStartDate || !effectiveEndDate) return [];

  return list.filter((loan) => {
    if (loan?.is_active === false) return false;
    if (String(loan?.deduct_from || '').trim().toLowerCase() !== recordSource) return false;
    const loanStart = String(loan?.start_date || '').trim();
    const loanEnd = String(loan?.end_date || '').trim();
    const loanStartDate = parseDate(loanStart);
    const loanEndDate = parseDate(loanEnd);
    if (loanStartDate && loanEndDate && !(loanStartDate <= effectiveEndDate && loanEndDate >= effectiveStartDate)) return false;
    return doesLoanMatchEmployee({ loan, employeeId, employeeName });
  });
};

export const simulateLoanSchedule = ({
  amount,
  deductFrom,
  startDate,
  deductPerCutoff,
  payrollFrequency,
  payrollOnceMonthTarget,
  payrollDeduct15th,
  payrollDeductEndOfMonth
}) => {
  const principal = toNumber(amount);
  const start = parseDate(startDate);
  const source = String(deductFrom || '').trim().toLowerCase();
  if (principal <= 0 || !start || (source !== 'payroll' && source !== 'mf')) {
    return { cutoffCount: 0, endDate: null };
  }

  if (source === 'mf') {
    const perCutoff = toNumber(deductPerCutoff);
    if (perCutoff <= 0) return { cutoffCount: 0, endDate: null };
    const count = Math.ceil(principal / perCutoff);
    return {
      cutoffCount: count,
      endDate: addMonths(start, count)
    };
  }

  const frequency = String(payrollFrequency || '').trim().toLowerCase();
  const onceTarget = String(payrollOnceMonthTarget || '').trim().toLowerCase();
  const sharedAmount = toNumber(deductPerCutoff);
  const amount15th = toNumber(payrollDeduct15th);
  const amountEnd = toNumber(payrollDeductEndOfMonth);

  let allowedHalves = new Set([HALF_FIRST, HALF_END]);
  if (frequency === 'once_month') {
    if (onceTarget === '15th') allowedHalves = new Set([HALF_FIRST]);
    else if (onceTarget === 'end_month') allowedHalves = new Set([HALF_END]);
    else return { cutoffCount: 0, endDate: null };
    if (sharedAmount <= 0) return { cutoffCount: 0, endDate: null };
  } else if (frequency === 'twice_month') {
    allowedHalves = new Set();
    if (amount15th > 0) allowedHalves.add(HALF_FIRST);
    if (amountEnd > 0) allowedHalves.add(HALF_END);
    if (allowedHalves.size === 0) return { cutoffCount: 0, endDate: null };
  } else {
    if (sharedAmount <= 0) return { cutoffCount: 0, endDate: null };
  }

  let remaining = principal;
  let cursor = start;
  let cutoffCount = 0;
  let lastEventDate = null;

  for (let i = 0; i < 480 && remaining > 0; i += 1) {
    const nextEvent = getNextPayrollEvent(cursor, allowedHalves);
    if (!nextEvent) break;

    let scheduledAmount = 0;
    if (frequency === 'once_month') {
      scheduledAmount = sharedAmount;
    } else if (frequency === 'twice_month') {
      scheduledAmount = nextEvent.half === HALF_FIRST ? amount15th : amountEnd;
    } else {
      scheduledAmount = sharedAmount;
    }

    if (scheduledAmount > 0 && matchesMonthlyTarget(nextEvent.date, nextEvent.half)) {
      remaining = Math.max(0, remaining - Math.min(scheduledAmount, remaining));
      cutoffCount += 1;
      lastEventDate = nextEvent.date;
    }

    cursor = new Date(nextEvent.date.getFullYear(), nextEvent.date.getMonth(), nextEvent.date.getDate() + 1);
  }

  return {
    cutoffCount,
    endDate: lastEventDate
  };
};
